import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { PolymorphicHTMLProps } from '@findigs/helpers/src/helpers/polymorphic';
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { IconButton } from '../icon-button';
import { Link } from '../link';
import { styles } from './toast.module.scss';
import ErrorIcon from '../../icons/material/error.round.inline.svg';
import CloseIcon from '../../images/close-icon.inline.svg';
import SuccessIcon from '../../images/findigs-checkmark.round.inline.svg';

export const DEFAULT_TOAST_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support if the problem persists.';

export const TOAST_VARIANT = ['Success', 'Error'] as const;

export type ToastVariant = (typeof TOAST_VARIANT)[number];

export type ToastAction = {
  label: string;
  href?: string;
  target?: '_blank' | AnyOtherString;
  rel?: 'noopener noreferrer' | AnyOtherString;
  onClick?: () => void;
};

export type ToastProps = PolymorphicHTMLProps & {
  open?: boolean;
  variant: ToastVariant;
  action?: ToastAction;
  withClose?: boolean;
  onClose?: () => void;
};

const Icons: { [key in ToastVariant]: () => JSX.Element } = {
  Success: () => <SuccessIcon width={24} height={24} aria-label="Success:" />,
  Error: () => <ErrorIcon width={24} height={24} aria-label="Error:" />,
};

export const Toast = polymorphic(
  { element: 'div', forwardRef: true },
  (
    {
      variant,
      action,
      withClose,
      onClose,
      className,
      children,
      open,
      ...props
    }: ToastProps,
    Component,
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    // We intentionally exclude `handleClose` from deps so that the timeout is
    // created only once per mount. Using `handleClose` (which closes over a
    // potentially changing `onClose` callback from parents) would restart the
    // timer every time the parent re-renders, preventing auto-dismiss when
    // multiple toasts are stacked.
    useEffect(() => {
      if (!open) return;

      setIsOpen(true);

      const closeOutDuration = action ? 9000 : withClose ? 6000 : 4000;
      const timer = setTimeout(() => {
        handleClose();
      }, closeOutDuration);

      return () => clearTimeout(timer);
      // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    }, [open, action, withClose]);

    const classes = classList(styles, variant, className, isOpen && 'open');

    return (
      <Component
        className={classes}
        {...props}
        ref={ref}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {Icons[variant]()}
        <div className="content">
          {typeof children === 'string' ? <p>{children}</p> : children}
          {action &&
            (action.href ? (
              <Link
                className="action"
                inverse
                href={action.href}
                target={action.target}
                rel={action.rel}
                onClick={action.onClick}
              >
                {action.label}
              </Link>
            ) : (
              <Link
                as="button"
                type="button"
                className="action"
                inverse
                onClick={action.onClick}
              >
                {action.label}
              </Link>
            ))}
          {withClose && (
            <IconButton
              className="toast-close-button"
              type="button"
              variant="Standard"
              size="Small"
              shape="Circle"
              onClick={handleClose}
            >
              <CloseIcon width={12} height={12} />
            </IconButton>
          )}
        </div>
      </Component>
    );
  }
);
